/**
 * rgApi — client API pour Refuge Guests (endpoints /api/rg/*).
 *
 * L'authentification est portée par le cookie JWT httpOnly du Hub :
 * `credentials: "include"` transmet automatiquement le cookie sur chaque
 * requête. Aucun token Bearer, aucun localStorage.
 *
 * En cas d'erreur 401, la promesse est rejetée avec un objet {status:401}
 * pour que l'appelant redirige vers /admin/login si besoin.
 */

const BASE = process.env.REACT_APP_BACKEND_URL;

async function rgFetch(path, options = {}) {
  const res = await fetch(`${BASE}/api/rg${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  if (!res.ok) {
    // Lire le body UNE SEULE FOIS (sinon "Body is disturbed or locked")
    const raw = await res.text();
    let detail = raw;
    try { detail = JSON.parse(raw).detail || raw; } catch { /* keep raw */ }
    const err = new Error(detail || `Erreur ${res.status}`);
    err.status = res.status;
    throw err;
  }
  // Certaines routes (ex: calendar/export.ics) renvoient du texte, pas du JSON
  const ct = res.headers.get("content-type") || "";
  return ct.includes("json") ? res.json() : res.text();
}

export const rgApi = {
  // Reservations
  listReservations: (future = true) => rgFetch(`/reservations?future_only=${future}`),
  getReservation: (id) => rgFetch(`/reservations/${id}`),
  createReservation: (data) => rgFetch(`/reservations`, { method: "POST", body: JSON.stringify(data) }),
  updateReservation: (id, data) => rgFetch(`/reservations/${id}`, { method: "PATCH", body: JSON.stringify(data) }),

  // Messages
  addMessage: (id, msg) => rgFetch(`/reservations/${id}/messages`, { method: "POST", body: JSON.stringify(msg) }),
  deleteMessage: (id, msgId) => rgFetch(`/reservations/${id}/messages/${msgId}`, { method: "DELETE" }),
  updateMessage: (id, msgId, data) => rgFetch(`/reservations/${id}/messages/${msgId}`, { method: "PATCH", body: JSON.stringify(data) }),

  // Demands (à traiter / traité)
  addDemand: (id, text) => rgFetch(`/reservations/${id}/demands`, { method: "POST", body: JSON.stringify({ text }) }),
  updateDemand: (id, dId, data) => rgFetch(`/reservations/${id}/demands/${dId}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteDemand: (id, dId) => rgFetch(`/reservations/${id}/demands/${dId}`, { method: "DELETE" }),

  // Couchage formulaire (URL statique unique — /formulaire-couchage)
  getCouchageAdminPhone: () => rgFetch(`/public/couchage-config`).then(d => d.admin_phone || ""),
  setCouchageAdminPhone: (admin_phone) =>
    rgFetch(`/settings/couchage-admin-phone`, { method: "PUT", body: JSON.stringify({ admin_phone }) }),

  // Views
  today: () => rgFetch(`/today`),
  calendarEvents: (from, to) => rgFetch(`/calendar/events?from_date=${from}&to_date=${to}`),

  // Templates / Kits
  getTemplates: () => rgFetch(`/messages/templates`),
  putTemplates: (data) => rgFetch(`/messages/templates`, { method: "PUT", body: JSON.stringify(data) }),
  getKits: () => rgFetch(`/kits`),
  putKits: (data) => rgFetch(`/kits`, { method: "PUT", body: JSON.stringify(data) }),

  // Prep Master List (Liste préparation jour J à imprimer)
  getPrepMasterList: () => rgFetch(`/prep-master-list`),
  putPrepMasterList: (data) => rgFetch(`/prep-master-list`, { method: "PUT", body: JSON.stringify(data) }),
  resetPrepMasterList: () => rgFetch(`/prep-master-list/reset`, { method: "POST" }),

  // Cleaning Protocol (Cockpit ménage — blocs & actions modifiables)
  getCleaningProtocol: () => rgFetch(`/cleaning-protocol`),
  putCleaningProtocol: (data) => rgFetch(`/cleaning-protocol`, { method: "PUT", body: JSON.stringify(data) }),
  resetCleaningProtocol: () => rgFetch(`/cleaning-protocol/reset`, { method: "POST" }),

  // Completions & shortages
  listCompletions: () => rgFetch(`/actions/completions`),
  setCompletion: (actionId, done) => rgFetch(`/actions/completions/${encodeURIComponent(actionId)}`, { method: "PUT", body: JSON.stringify({ done }) }),
  getKitShortages: () => rgFetch(`/kit-shortages`),
  putKitShortages: (data) => rgFetch(`/kit-shortages`, { method: "PUT", body: JSON.stringify(data) }),

  // CSV import — multipart, request custom
  importCsv: async (file, filename, mode /* "merge" | "replace" */) => {
    const form = new FormData();
    form.append("file", file, filename);
    form.append("mode", mode);
    const res = await fetch(`${BASE}/api/rg/csv/import`, {
      method: "POST",
      credentials: "include",
      body: form,
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(t || `Erreur ${res.status}`);
    }
    return res.json();
  },

  // Meta
  syncMeta: () => rgFetch(`/meta/sync`),

  // ICS calendar export URL (uses dedicated token because Apple Calendar can't send cookies)
  icsExportUrl: (icsToken) => `${BASE}/api/rg/calendar/export.ics?token=${encodeURIComponent(icsToken || "curieux-refuge-token-v1")}`,

  // vCard export (cookie auth suffit — utilisé depuis la fiche voyageur)
  vcardUrl: (id) => `${BASE}/api/rg/reservations/${id}/vcard.vcf`,

  // Breakfast form sync (Google Sheet)
  syncBreakfast: () => rgFetch(`/breakfast/sync`, { method: "POST" }),

  // Formulaire petit-déjeuner natif (lien individuel par réservation)
  getBreakfastLink: (id) => rgFetch(`/reservations/${id}/breakfast-link`),
  getCouchageLink: (id) => rgFetch(`/reservations/${id}/couchage-link`),
  getBainLink: (id) => rgFetch(`/reservations/${id}/bain-link`),
  getBreakfastConfig: () => rgFetch(`/breakfast/config`),
  saveBreakfastConfig: (cfg) => rgFetch(`/breakfast/config`, { method: "PUT", body: JSON.stringify(cfg) }),

  // Lodgify sync
  lodgifyStatus: () => rgFetch(`/lodgify/status`),
  lodgifyTestConnection: () => rgFetch(`/lodgify/test-connection`),
  lodgifyBackfill: () => rgFetch(`/lodgify/backfill`, { method: "POST" }),
  lodgifySubscribe: (target_url) => rgFetch(`/lodgify/subscribe`, { method: "POST", body: JSON.stringify({ target_url }) }),
  lodgifyUnsubscribe: () => rgFetch(`/lodgify/unsubscribe`, { method: "POST" }),
  lodgifyWebhooksRemote: () => rgFetch(`/lodgify/webhooks/list-remote`),
  lodgifyWebhooksHardReset: (target_url) => rgFetch(`/lodgify/webhooks/hard-reset`, { method: "POST", body: JSON.stringify({ target_url }) }),
  lodgifySyncMessages: (reservationId) => rgFetch(`/lodgify/messages/sync/${reservationId}`, { method: "POST" }),
  lodgifySyncClosedPeriods: () => rgFetch(`/lodgify/closed-periods/sync`, { method: "POST" }),

  // Web Push (admin notifications)
  pushPublicKey: () => rgFetch(`/push/public-key`),
  pushSubscribe: (subscription) => rgFetch(`/push/subscribe`, { method: "POST", body: JSON.stringify(subscription) }),
  pushUnsubscribe: (subscription) => rgFetch(`/push/unsubscribe`, { method: "POST", body: JSON.stringify(subscription) }),
  pushStatus: () => rgFetch(`/push/status`),
  pushTest: () => rgFetch(`/push/test`, { method: "POST" }),

  // Cleaning blocs — chronométrage & records
  cleaningStats: () => rgFetch(`/cleaning-stats`),
  updateCleaningTarget: (blocKey, target_sec) =>
    rgFetch(`/cleaning-stats/target/${blocKey}`, { method: "PUT", body: JSON.stringify({ target_sec }) }),
  updateCleaningRecord: (blocKey, duration_sec) =>
    rgFetch(`/cleaning-stats/record/${blocKey}`, { method: "PUT", body: JSON.stringify({ duration_sec }) }),
  saveCleaningSession: (resId, blocKey, body) =>
    rgFetch(`/reservations/${resId}/cleaning-sessions/${blocKey}`, { method: "PUT", body: JSON.stringify(body) }),

  // iCloud CalDAV (calendrier personnel privé — lecture seule, OFF + PERSO anonymisés)
  icloudStatus: () => rgFetch(`/icloud/status`),
  icloudSetCredentials: (apple_id, app_password) =>
    rgFetch(`/icloud/credentials`, { method: "POST", body: JSON.stringify({ apple_id, app_password }) }),
  icloudTest: () => rgFetch(`/icloud/test`, { method: "POST" }),
  icloudSetCalendars: (urls) =>
    rgFetch(`/icloud/calendars`, { method: "PUT", body: JSON.stringify({ selected_calendars: urls }) }),
  icloudSync: () => rgFetch(`/icloud/sync`, { method: "POST" }),
  icloudDisconnect: () => rgFetch(`/icloud/disconnect`, { method: "DELETE" }),
  icloudEvents: (date_from, date_to) =>
    rgFetch(`/icloud/events?date_from=${date_from}&date_to=${date_to}`),

  // Closed periods (fermetures)
  listClosedPeriods: () => rgFetch(`/closed-periods`),
  createClosedPeriod: (body) => rgFetch(`/closed-periods`, { method: "POST", body: JSON.stringify(body) }),
  updateClosedPeriod: (id, body) => rgFetch(`/closed-periods/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteClosedPeriod: (id) => rgFetch(`/closed-periods/${id}`, { method: "DELETE" }),
  importBreakfastCsv: async (file) => {
    const fd = new FormData();
    fd.append("file", file, file.name || "breakfast.csv");
    // FormData: on ne met PAS le Content-Type (le navigateur pose la boundary)
    const res = await fetch(`${BASE}/api/rg/breakfast/import-csv`, {
      method: "POST",
      credentials: "include",
      body: fd,
    });
    if (!res.ok) {
      let detail;
      try { detail = (await res.json()).detail; } catch { detail = await res.text(); }
      const err = new Error(detail || `Erreur ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  },
};

// Constantes UI partagées avec Refuge Guests
export const EXTRA_LABELS = {
  bain_fleurs: "Bain de fleurs",
  petit_dejeuner: "Petit déjeuner",
  decor: "Décor du refuge",
  troisieme_lit: "3e lit",
};
export const EXTRA_KEYS = ["bain_fleurs", "petit_dejeuner", "decor"];
export const EXTRA_ICONS = {
  bain_fleurs: "🌸",
  petit_dejeuner: "🥐",
  decor: "🌹",
  troisieme_lit: "🛏️",
};
export const EXTRA_NEEDS = {
  bain_fleurs: "20 roses roses",
  petit_dejeuner: "Liste à faire",
  decor: "8 roses",
  troisieme_lit: "Lit parapluie ou d'appoint",
};

// Formatage court : "mar 15 août"
const DAYS_SHORT = ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"];
const MONTHS_SHORT = ["jan", "fév", "mars", "avr", "mai", "juin", "juil", "août", "sept", "oct", "nov", "déc"];
export function fmtShortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  return `${DAYS_SHORT[d.getDay()]} ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
}
const DAYS_LONG = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const MONTHS_LONG = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
export function fmtLongDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  return `${DAYS_LONG[d.getDay()]} ${d.getDate()} ${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`;
}
export function firstName(full) {
  return (full || "").trim().split(/\s+/)[0] || "";
}
